/**
 * The activity and audit timeline, on real data.
 *
 * One component serves both tabs; they differ only in which event kinds they ask for. That is deliberate —
 * "activity" and "audit" are the same history read at different altitudes, and having two components would
 * eventually mean two answers to the same question.
 *
 * What makes it worth reading rather than merely complete:
 *  - every entry says who, when, from where, and why, because "why" is the column people actually need
 *    six months later;
 *  - a change shows only the columns that moved, not a whole-row dump;
 *  - a mirrored record is visibly different from a hand-entered one;
 *  - the correlation id is one click from "what else came from that same read?".
 */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  ArrowRight, CircleAlert, FileDiff, GitBranch, Hourglass, Landmark, RefreshCcw,
} from 'lucide-react';
import { rpc } from '../lib/supabase';
import { EmptyState, ErrorState, LoadingRows, SectionLabel, cx } from '../ui/primitives';
import type { TimelineEvent } from './types';
import { dateTime as fmtDateTime } from '../ui/format';

const KIND_META: Record<
  TimelineEvent['kind'],
  { label: string; icon: typeof Landmark; dot: string; tint: string }
> = {
  BUSINESS: { label: 'Business', icon: Landmark, dot: 'bg-series-1', tint: 'text-ink' },
  INTEGRATION: { label: 'Integration', icon: RefreshCcw, dot: 'bg-info', tint: 'text-info' },
  CHANGE: { label: 'Change', icon: FileDiff, dot: 'bg-line-strong', tint: 'text-ink-muted' },
  APPROVAL: { label: 'Approval', icon: Hourglass, dot: 'bg-queued', tint: 'text-queued' },
  EXCEPTION: { label: 'Exception', icon: CircleAlert, dot: 'bg-refused', tint: 'text-refused' },
};

const ALL_KINDS: TimelineEvent['kind'][] = ['BUSINESS', 'INTEGRATION', 'CHANGE', 'APPROVAL', 'EXCEPTION'];

function formatValue(value: unknown): string {
  if (value === null || value === undefined) return '—';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

/** Shows only what moved. A whole-row dump on every update is unreadable and goes unread. */
function Diff({ previous, next }: { previous: Record<string, unknown> | null; next: Record<string, unknown> | null }) {
  const keys = [...new Set([...Object.keys(previous ?? {}), ...Object.keys(next ?? {})])]
    // Bookkeeping columns move on every write and say nothing about the business.
    .filter((k) => !['updated_at', 'created_at', 'row_version', 'updated_by', 'created_by'].includes(k))
    .sort();

  if (keys.length === 0) return null;

  return (
    <dl className="mt-1 grid gap-x-3 gap-y-0.5" style={{ gridTemplateColumns: 'auto 1fr' }}>
      {keys.slice(0, 8).map((key) => {
        const before = previous?.[key];
        const after = next?.[key];
        const changed = JSON.stringify(before) !== JSON.stringify(after);
        return (
          <div key={key} className="contents">
            <dt className="code-text text-ink-faint">{key}</dt>
            <dd className="min-w-0 truncate text-small">
              {previous && changed ? (
                <>
                  <span className="text-ink-faint line-through">{formatValue(before)}</span>
                  <ArrowRight size={10} className="mx-1 inline text-ink-faint" aria-hidden />
                  <span className="text-ink">{formatValue(after)}</span>
                </>
              ) : (
                <span className="text-ink">{formatValue(after ?? before)}</span>
              )}
            </dd>
          </div>
        );
      })}
      {keys.length > 8 ? (
        <div className="contents">
          <dt />
          <dd className="text-small text-ink-faint">and {keys.length - 8} more</dd>
        </div>
      ) : null}
    </dl>
  );
}

export function EntityTimeline({
  entityType, entityId, kinds, title, emptyLabel,
}: {
  entityType: string;
  entityId: string;
  /** Omit for everything. The audit tab passes CHANGE; the activity tab passes the rest. */
  kinds?: TimelineEvent['kind'][];
  title?: string;
  emptyLabel?: string;
}) {
  const [filter, setFilter] = useState<TimelineEvent['kind'] | 'ALL'>('ALL');

  const query = useQuery({
    queryKey: ['entity-timeline', entityType, entityId, kinds?.join(',') ?? 'all'],
    queryFn: () =>
      rpc<TimelineEvent[]>('entity_timeline', {
        p_entity_type: entityType,
        p_entity_id: entityId,
        p_limit: 200,
        p_kinds: kinds ?? null,
      }),
  });

  if (query.isLoading) return <LoadingRows rows={5} columns={3} />;
  if (query.isError) return <ErrorState error={query.error} retry={() => void query.refetch()} />;

  const events = query.data ?? [];
  const available = ALL_KINDS.filter((k) => events.some((e) => e.kind === k));
  const shown = filter === 'ALL' ? events : events.filter((e) => e.kind === filter);

  if (events.length === 0) {
    return (
      <EmptyState
        title={emptyLabel ?? 'Nothing recorded yet'}
        detail="Events appear here as the record is created, changed, mirrored from a field application, approved or disputed."
      />
    );
  }

  return (
    <div>
      {available.length > 1 ? (
        <div className="flex items-center gap-1 border-b border-line px-4 py-1.5">
          {title ? <SectionLabel className="mr-2">{title}</SectionLabel> : null}
          {(['ALL', ...available] as const).map((kind) => (
            <button
              key={kind}
              type="button"
              onClick={() => setFilter(kind)}
              className={cx(
                'rounded-[var(--radius-control)] px-2 py-0.5 text-small transition-colors duration-[120ms]',
                filter === kind ? 'bg-brand-soft font-medium text-brand' : 'text-ink-muted hover:bg-surface-sunken',
              )}
            >
              {kind === 'ALL' ? 'All' : KIND_META[kind].label}
              <span className="ml-1 tabular text-ink-faint">
                {kind === 'ALL' ? events.length : events.filter((e) => e.kind === kind).length}
              </span>
            </button>
          ))}
        </div>
      ) : null}

      <ol className="relative px-4 py-3">
        <span className="absolute top-3 bottom-3 left-[1.35rem] w-px bg-line" aria-hidden />
        {shown.map((event, index) => {
          const meta = KIND_META[event.kind];
          const Icon = meta.icon;
          return (
            <li key={`${event.occurred_at}-${event.event}-${index}`} className="relative flex gap-3 py-2">
              <span
                className={cx('relative z-10 mt-1.5 h-2 w-2 shrink-0 rounded-full ring-2 ring-surface', meta.dot)}
                aria-hidden
              />
              <div className="min-w-0 flex-1">
                <div className="flex items-baseline justify-between gap-3">
                  <span className="flex min-w-0 items-center gap-1.5">
                    <Icon size={12} className={cx('shrink-0', meta.tint)} aria-hidden />
                    <span className="code-text truncate">{event.event}</span>
                    {/* Colour is never the only signal: the kind is written out. */}
                    <span className="label-text shrink-0">{meta.label}</span>
                  </span>
                  <time className="shrink-0 font-mono text-badge text-ink-faint" dateTime={event.occurred_at}>
                    {fmtDateTime(event.occurred_at)}
                  </time>
                </div>

                <Diff previous={event.previous_state} next={event.new_state} />

                <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-small text-ink-faint">
                  <span>{event.actor}</span>
                  {event.source_system !== 'erp' && event.source_system !== 'system' ? (
                    <span className="code-text">{event.source_system}</span>
                  ) : null}
                  {/* The reason is the column people actually need six months later. */}
                  {event.reason ? <span className="italic text-ink-muted">“{event.reason}”</span> : null}
                  {event.correlation_id ? (
                    <span
                      className="code-text inline-flex items-center gap-1"
                      title="Correlation id — everything that arrived in the same connector run"
                    >
                      <GitBranch size={10} aria-hidden />
                      {event.correlation_id.slice(0, 8)}
                    </span>
                  ) : null}
                </div>
              </div>
            </li>
          );
        })}
      </ol>
    </div>
  );
}
